import { translate } from '@teamleader/i18n';
import { IconChevronDownSmallOutline, IconChevronUpSmallOutline } from '@teamleader/ui-icons';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useIsSmall } from '../../../../Components/Context/Hooks/mediaQueries';
import Box, { BoxProps } from '../../../../Components/UI/Box';
import { MenuItem } from '../../../../Components/UI/DropdownButton';
import Popover from '../../../../Components/UI/Popover';
import { Quotation } from '../../../../Types/quotation';
import { EVENTS, track } from '../../../../Utils/analytics';
import { DealContext } from '../../Context';
import styles from '../styles.css';

interface QuotationNameProps extends BoxProps {
  quotation: Quotation;
}

const QuotationName: React.FC<QuotationNameProps> = ({ quotation }) => {
  const { token } = useParams<HomeRouteProps>();
  const { deal } = useContext(DealContext);
  const [isOpen, setIsOpen] = useState(false);
  const isSmall = useIsSmall();
  const ref = useRef<HTMLButtonElement>(null);

  const handleOpenDropdown = useCallback(() => setIsOpen(true), []);
  const handleCloseDropdown = useCallback(() => setIsOpen(false), []);

  const amount = deal!.quotations.length;
  const index = useMemo(() => deal!.quotations.indexOf(quotation) + 1, [deal, quotation]);
  const dropdownVisible = isSmall && amount > 1;
  const Icon = isOpen ? IconChevronUpSmallOutline : IconChevronDownSmallOutline;

  const onQuotationClick = (quotationId: string) => {
    track(EVENTS.SELECTED_QUOTATION, { quotation: quotationId, fromHeader: true });
    handleCloseDropdown();
  };

  return (
    <Box className={styles['title']}>
      <Box element="h1" display="flex" alignItems="center" onClick={isSmall ? handleOpenDropdown : undefined} ref={ref}>
        <span className={styles['title-text']} title={quotation.name}>
          {quotation.name}
        </span>
        {dropdownVisible && <Icon className={styles['caret']} />}
      </Box>
      {dropdownVisible && (
        <Box className={styles['document-count']}>{translate('quotationXOfX', { index, amount })}</Box>
      )}
      {ref.current && isOpen && (
        <Popover
          anchor={ref.current}
          onEscKeyDown={handleCloseDropdown}
          onOverlayClick={handleCloseDropdown}
          placement="bottom-start"
        >
          <Box element="ul" marginVertical={2} className={styles['document-list']}>
            {deal!.quotations.map((dealQuotation) => (
              <MenuItem
                key={dealQuotation.id}
                element={Link}
                to={`/${token || ''}/${deal!.id}/${dealQuotation.id}`}
                onClick={() => onQuotationClick(dealQuotation.id)}
                selected={dealQuotation.id === quotation.id}
                title={dealQuotation.name}
              >
                {dealQuotation.name}
              </MenuItem>
            ))}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

export default QuotationName;
